/**
 * Every visible string for following other fans (ISSUE-1370, ISSUE-1372),
 * declared once so the profile control and the Followers screen say the
 * same thing. American English, straight apostrophes, no dashes.
 */
import type { typeFollowState } from "./followState";

export const FOLLOW_LABEL = "Follow";
export const FOLLOW_BACK_LABEL = "Follow back";
export const REQUESTED_LABEL = "Requested";
export const FOLLOWING_LABEL = "Following";
export const FOLLOWS_YOU_LABEL = "Follows you";

export const UNFOLLOW_CONFIRM_LABEL = "Unfollow";
export const UNFOLLOW_CONFIRM_BODY =
  "They won't be told. You can follow them again any time.";
export const WITHDRAW_CONFIRM_LABEL = "Withdraw request";
export const WITHDRAW_CONFIRM_BODY =
  "They won't see your request anymore. You can ask again later.";
export const REMOVE_FOLLOWER_CONFIRM_LABEL = "Remove";
export const REMOVE_FOLLOWER_CONFIRM_BODY =
  "They won't be told, and they can ask to follow you again.";

export function unfollowConfirmTitle(displayName: string): string {
  return `Unfollow ${displayName}?`;
}

export function withdrawConfirmTitle(displayName: string): string {
  return `Withdraw your request to ${displayName}?`;
}

export function removeFollowerConfirmTitle(displayName: string): string {
  return `Remove ${displayName} from your followers?`;
}

/** The label a follow button shows for the viewer's current state. */
export function followButtonLabel(
  state: typeFollowState,
  options: { followsYou?: boolean } = {},
): string {
  if (state === "following") return FOLLOWING_LABEL;
  if (state === "requested") return REQUESTED_LABEL;
  return options.followsYou === true ? FOLLOW_BACK_LABEL : FOLLOW_LABEL;
}

/**
 * What the screen reader hears, and the line under the button says, after a
 * tap. `previous` is the state the tap started from: withdrawing a request
 * you never had approved is not "no longer following" (QA round 1).
 */
export function followChangedAnnouncement(
  state: typeFollowState,
  displayName: string,
  previous?: typeFollowState,
): string {
  if (state === "following") return `You're now following ${displayName}.`;
  if (state === "requested")
    return `Request sent. ${displayName} will see it and can approve it.`;
  if (previous === "requested")
    return `Request withdrawn. ${displayName} won't see it anymore.`;
  return `You're no longer following ${displayName}.`;
}

// The Followers screen (ISSUE-1372).
export const FOLLOWERS_HEADING = "Followers";
export const FOLLOWERS_BACK_LABEL = "Back to Account";
export const FOLLOWERS_TABS_LABEL = "Followers and following";
export const REQUESTS_TAB_LABEL = "Requests";
export const FOLLOWERS_TAB_LABEL = "Followers";
export const FOLLOWING_TAB_LABEL = "Following";
export const APPROVE_LABEL = "Approve";
export const DECLINE_LABEL = "Decline";
export const REMOVE_LABEL = "Remove";
export const UNFOLLOW_LABEL = "Unfollow";
export const WITHDRAW_LABEL = "Withdraw";
export const MESSAGE_LABEL = "Message";
export const SHOW_MORE_LABEL = "Show more";
export const WAITING_SECTION_TITLE = "Waiting for approval";
export const REQUESTS_EMPTY_HEADLINE = "No follow requests";
export const REQUESTS_EMPTY_BODY =
  "When a fan asks to follow you, you'll approve them here.";
export const FOLLOWERS_EMPTY_HEADLINE = "No followers yet";
export const FOLLOWERS_EMPTY_BODY =
  "Fans who follow you show up here. You can remove anyone, any time.";
export const FOLLOWING_EMPTY_HEADLINE = "You're not following anyone yet";
export const FOLLOWING_EMPTY_BODY =
  "Open a fan's profile from your pool or a chat and tap Follow.";

export function openProfileLabel(displayName: string): string {
  return `Open ${displayName}'s profile`;
}

export function messageLabelFor(displayName: string): string {
  return `Message ${displayName}`;
}
